// [Basis] Consentimiento de cookies y analítica, compartidos con la landing.
//
// Port de frontend_landing/src/lib/analytics.ts. Mismo modelo de cookies, misma
// resolución y mismo trato a Clarity; lo que cambia es el idioma del fichero y
// que aquí no hay catálogo de eventos, porque el explorador no instrumenta nada
// propio: se mira con grabaciones y mapas de calor, no con embudos.
//
// La analítica va encendida por defecto y el aviso lo dice: seguir navegando ES
// la aceptación. «Aceptar» solo lo deja por escrito —no arranca nada que no
// estuviera ya corriendo— y «Rechazar» es el botón que hace algo.
//
// Y rechazar hay que tomárselo al pie de la letra, porque el "Consent Mode" de
// Clarity solo le impide escribir cookies: la etiqueta sigue ejecutándose y
// sigue enviando a /collect, IP incluida. Por eso un rechazo descarta el
// documento entero en vez de fiarse de ese interruptor.

// El proyecto de Clarity del explorador. La landing y el faucet tienen el suyo:
// son tres sitios distintos y cada uno se mira por separado. Va aquí y no en una
// variable de entorno a propósito — es un identificador público que viaja en el
// bundle de todas formas, y una env de Next hay que declararla en dos ficheros
// de despliegue distintos para que llegue a la imagen.
const ID_CLARITY = 'y86ivjzaqm';

export type ConsentState = 'granted' | 'denied' | 'unknown';

/** Una decisión guardada: qué se eligió, y cuándo. */
interface Decision {
  state: 'granted' | 'denied';

  /** Segundos desde la época, del reloj del propio navegador. */
  at: number;
}

/** Qué toca hacer ahora mismo, resuelto de las dos cookies. */
export interface Resolution {

  /** El consentimiento en vigor aquí. 'unknown' es que nadie ha dicho nada. */
  state: ConsentState;

  /** Si además hay que enseñar el aviso, porque aquí no se decidió nada. */
  ask: boolean;

  /**
   * Si la analítica corre en esta carga. Cierto salvo que se haya rechazado: no
   * decir nada no es un rechazo, es la aceptación que el aviso anuncia.
   */
  run: boolean;
}

// ---------------------------------------------------------------------------
// Dos cookies, y la separación es justo el asunto.
//
//   basis_consent      solo de este host. La decisión tomada EN ESTE SITIO.
//   basis_consent_net  Domain=basisnetwork.com.co. La última decisión tomada en
//                      CUALQUIERA de los tres sitios de ese dominio.
//
// Para quien visita, la landing, el faucet y el explorador son un mismo
// producto, así que un «Aceptar» en cualquiera de ellos calla el aviso en los
// tres sin volver a preguntar. Un «Rechazar» no es su reflejo: apaga la
// analítica en los tres, pero solo calla el aviso en aquel donde se pulsó. Los
// otros dos vuelven a preguntar, una vez, y mientras preguntan la etiqueta
// tampoco carga: el rechazo está en vigor en toda la red aunque no se haya
// repetido aquí. Rechazar el explorador no es la misma frase que rechazar la red
// entera, y esta asimetría es donde queda escrito.
//
// Los empates y los cambios de opinión los resuelve la marca de tiempo, no una
// precedencia: gana la decisión más reciente. A los dos lados de la comparación
// hay siempre el mismo reloj —el del navegador—, así que un reloj desajustado no
// puede alterar el orden de dos decisiones.
// ---------------------------------------------------------------------------

const COOKIE_NAME = 'basis_consent';
const NETWORK_COOKIE_NAME = 'basis_consent_net';

// Dominios cuyo subárbol comparte una decisión. `basis.localhost` no es una
// errata ni sobra: Chrome y Firefox resuelven cualquier etiqueta *.localhost a
// la dirección de loopback sin tocar /etc/hosts, así que el banco de pruebas
// local ejercita este mismo camino —dominio de la cookie incluido— en vez de un
// caso especial que solo corre en la máquina de quien lo escribió.
const SHARED_DOMAINS = [ 'basisnetwork.com.co', 'basis.localhost' ];

// La versión 1 es la cookie que la landing escribía antes de que existiera la de
// red: no lleva marca de tiempo y se lee como `at: 0`, más antigua que cualquier
// decisión que pueda tomarse a partir de ahora. Que es lo correcto: a quien ya
// aceptó no se le vuelve a preguntar, y un rechazo posterior en otro sitio sigue
// ganándole.
const COOKIE_VERSION = 2;
const LEGACY_COOKIE_VERSION = 1;

// 180 días. La CNIL recomienda no repreguntar antes de seis meses, y así un
// rechazo no se vuelve a discutir en cada visita.
const COOKIE_MAX_AGE = 15552000;

// Cookies que Clarity deja. Se barren al revocar: quien dijo que no, no debe
// quedarse con un `_clck` identificándolo.
const CLARITY_COOKIES = [ '_clck', '_clsk', 'CLID', 'ANONCHK', 'MR', 'MUID', 'SM' ];

const isState = (value: string): value is 'granted' | 'denied' =>
  value === 'granted' || value === 'denied';

const now = (): number => Math.floor(Date.now() / 1000);

/**
 * El dominio con el que este host comparte decisión, o null si no hay ninguno
 * —una vista previa, una IP, un `localhost` pelado—. Null no es un fallo: la
 * cookie de red se escribe entonces solo para este host y el sitio se comporta
 * como una isla, que es lo que significa «no hay nadie más bajo este nombre».
 */
function sharedDomain(): string | null {
  if (typeof location === 'undefined') {
    return null;
  }
  const host = location.hostname;
  return SHARED_DOMAINS.find((domain) => host === domain || host.endsWith(`.${ domain }`)) ?? null;
}

/** Lee una cookie de decisión. Lo que no se entienda cuenta como no decidido. */
function readDecision(name: string): Decision | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const entry = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${ name }=`));
  if (!entry) {
    return null;
  }

  const parts = decodeURIComponent(entry.slice(name.length + 1)).split(':');
  const version = Number(parts[0]);
  const state = parts[1] ?? '';
  if (!isState(state)) {
    return null;
  }

  if (version === COOKIE_VERSION) {
    const at = Number(parts[2]);
    return { state, at: Number.isFinite(at) && at > 0 ? at : 0 };
  }
  if (version === LEGACY_COOKIE_VERSION) {
    return { state, at: 0 };
  }
  return null;
}

function writeDecision(name: string, state: 'granted' | 'denied', at: number, domain?: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  const scope = domain ? `; Domain=${ domain }` : '';
  document.cookie =
    `${ name }=${ COOKIE_VERSION }:${ state }:${ at }; Path=/; Max-Age=${ COOKIE_MAX_AGE }; SameSite=Lax${ secure }${ scope }`;
}

/**
 * Qué hacer en esta carga, leyendo las dos cookies a la vez.
 *
 * Las tres reglas, en el orden en que se aplican:
 *
 *   1. La decisión tomada AQUÍ manda mientras sea al menos tan reciente como la
 *      compartida. Reabrir el panel en este sitio y guardar es la última palabra
 *      en este sitio, y ningún otro subdominio la deshace en silencio.
 *   2. Si no, un «granted» compartido más reciente enciende la analítica sin
 *      avisar: acaban de aceptar, un subdominio más allá.
 *   3. Si no, un «denied» compartido más reciente revoca también aquí, pero NO
 *      hace de respuesta: se enseña el aviso, una vez, para no extender en
 *      silencio un rechazo a un sitio para el que nunca se dio.
 *   4. Y sin nada guardado en ninguna parte, la etiqueta corre y el aviso se
 *      enseña a la vez. Ese par es el modelo entero: el aviso no es la pregunta
 *      que hay que responder antes de empezar, es lo que hace que seguir
 *      navegando cuente como respuesta.
 */
export function resolveConsent(): Resolution {
  const local = readDecision(COOKIE_NAME);
  const network = readDecision(NETWORK_COOKIE_NAME);

  if (local && (!network || local.at >= network.at)) {
    return { state: local.state, ask: false, run: local.state === 'granted' };
  }
  if (network) {
    return network.state === 'granted' ?
      { state: 'granted', ask: false, run: true } :
      { state: 'denied', ask: true, run: false };
  }
  return { state: 'unknown', ask: true, run: true };
}

/**
 * Borrado, en la medida de lo posible, de las cookies que deja Clarity. Se hace
 * solo al rechazar —nunca en una visita sin decisión, donde la etiqueta SÍ está
 * corriendo y barrer borraría el `_clck` que acaba de escribir, convirtiendo a
 * quien vuelve en un visitante nuevo en cada página—.
 *
 * Las de tercera parte (MUID, CLID…) viven en dominios de Microsoft y desde aquí
 * no se alcanzan: solo desaparecen sus hermanas de primera parte. La política lo
 * dice.
 *
 * El dominio padre sale de `sharedDomain()` y NO se recalcula del host: el
 * `hostname.split('.').slice(-2)` evidente da `com.co`, que es un sufijo
 * público, así que el navegador descarta en silencio esos borrados. Y hace
 * falta de verdad: `_clck` y `_clsk` los escribe Clarity en el dominio padre,
 * medido, no supuesto — o sea que los comparten los tres sitios.
 */
export function sweepClarityCookies(): void {
  if (typeof document === 'undefined') {
    return;
  }
  const host = location.hostname;
  const domains = [ '', `; Domain=${ host }`, `; Domain=.${ host }` ];
  const parent = sharedDomain();
  if (parent && parent !== host) {
    domains.push(`; Domain=${ parent }`, `; Domain=.${ parent }`);
  }

  for (const name of CLARITY_COOKIES) {
    for (const domain of domains) {
      document.cookie = `${ name }=; Path=/; Max-Age=0; SameSite=Lax${ domain }`;
    }
  }
}

// ---------------------------------------------------------------------------
// Clarity
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    clarity?: ((...args: Array<unknown>) => void) & { q?: Array<unknown> };
  }
}

let injected = false;
let optedOut = false;

function callClarity(...args: Array<unknown>): void {
  if (optedOut || typeof window.clarity !== 'function') {
    return;
  }
  // Se traga cualquier excepción suya: Clarity no puede romper la página.
  try {
    window.clarity(...args);
  } catch (error) {}
}

/**
 * Inyecta la etiqueta. Es el fragmento oficial, con la misma forma: define
 * window.clarity como una cola, así que lo que se llame antes de que llegue el
 * script remoto se reproduce cuando llega.
 */
function injectClarity(): void {
  if (injected || !ID_CLARITY) {
    return;
  }
  injected = true;

  window.clarity = window.clarity || function(...args: Array<unknown>) {
    (window.clarity!.q = window.clarity!.q || []).push(args);
  };

  const tag = document.createElement('script');
  tag.async = true;
  tag.src = `https://www.clarity.ms/tag/${ ID_CLARITY }`;
  document.head.appendChild(tag);

  // ad_Storage se queda denegado porque no hacemos publicidad, y MUID/CLID, que
  // son de Microsoft, son cookies publicitarias. Su documentación escribe las
  // claves con S mayúscula mientras su propio verificador las lee en minúscula,
  // así que se mandan las dos formas.
  callClarity('consentv2', { ad_Storage: 'denied', analytics_Storage: 'granted' });
  callClarity('consentv2', { ad_storage: 'denied', analytics_storage: 'granted' });

  callClarity('set', 'site', 'explorer');
}

/** Enciende la analítica. Idempotente. */
export function enableAnalytics(): void {
  optedOut = false;
  if (injected) {
    callClarity('consentv2', { ad_Storage: 'denied', analytics_Storage: 'granted' });
    callClarity('consentv2', { ad_storage: 'denied', analytics_storage: 'granted' });
    return;
  }
  injectClarity();
}

/**
 * Apaga la analítica y dice si hace falta recargar para que eso sea verdad. Con
 * la analítica encendida por defecto esa recarga es el camino habitual y no la
 * excepción: en una primera visita Clarity ya está en el documento cuando
 * alguien llega al botón de rechazar.
 *
 * `clarity("consent", false)` NO detiene la etiqueta. Comprobado contra Clarity
 * 0.8.69: derriba la sesión y la reinicia 250 ms después en modo sin cookies, y
 * sigue enviando a /collect. La llamada vale la pena igual, pero lo único que la
 * para de verdad una vez está en el documento es descartar el documento.
 */
export function disableAnalytics(): boolean {
  optedOut = true;

  const wasRunning = injected;
  if (wasRunning && typeof window.clarity === 'function') {
    // Si la llamada falla da igual: lo que le importa a quien revoca es el
    // barrido de cookies y la recarga, que van después pase lo que pase.
    try {
      window.clarity('consent', false);
    } catch (error) {}
  }
  sweepClarityCookies();
  return wasRunning;
}

/**
 * Guarda la decisión y la aplica. Devuelve true cuando hay que recargar para que
 * una revocación surta efecto (ver disableAnalytics).
 *
 * Las dos cookies se escriben en cada decisión, rechazos incluidos: la de red es
 * lo que para a Clarity en los otros dos sitios, y no escribirla ahí dejaría un
 * «granted» viejo sobreviviendo al rechazo que lo revocó.
 *
 * Aceptar casi nunca hace nada del lado de la analítica —la etiqueta lleva
 * corriendo desde que cargó la página—. Lo que cambia es el registro, y con él
 * el aviso, aquí y en los otros dos sitios.
 */
export function setConsent(state: 'granted' | 'denied'): boolean {
  const at = now();
  writeDecision(COOKIE_NAME, state, at);
  writeDecision(NETWORK_COOKIE_NAME, state, at, sharedDomain() ?? undefined);

  if (state === 'granted') {
    enableAnalytics();
    callClarity('event', 'consent_granted');
    return false;
  }
  return disableAnalytics();
}
